import type { DesignThemeCssTokens } from './design-theme-css-tokens.types';

/** Saved IDs stay stable; every preset owns both of its palettes. */
type Palette = {
  bg: string;
  panel: string;
  card: string;
  rail: string;
  input: string;
  ink: string;
  secondaryInk: string;
  mutedInk: string;
  action: string;
  actionHover: string;
  onAction: string;
  accent: string;
};

function alpha(hex: string, opacity: number): string {
  const channels = [1, 3, 5].map(offset => parseInt(hex.slice(offset, offset + 2), 16));
  return `rgba(${channels.join(', ')}, ${opacity})`;
}

function theme(base: DesignThemeCssTokens, p: Palette, dark: boolean): DesignThemeCssTokens {
  const neutral = dark ? '#FFFFFF' : p.ink;
  return {
    ...base,
    bg: p.bg,
    bgWash: p.bg,
    bgDepth: p.bg,
    ambientGlow: 'transparent',
    meshDot: alpha(neutral, 0.08),
    glassBg: p.panel,
    glassBgSubtle: p.panel,
    glassBgMuted: p.input,
    glassBgBase: p.panel,
    glassBgElevated: p.card,
    glassBgCard: p.card,
    glassFill: p.input,
    glassInteractiveHover: alpha(neutral, 0.045),
    glassTraceHeaderHover: alpha(neutral, 0.045),
    glassBorder: alpha(neutral, dark ? 0.14 : 0.18),
    glassBorderSubtle: alpha(neutral, dark ? 0.08 : 0.09),
    uiSurfacePanel: p.panel,
    uiSurfaceCard: p.card,
    uiSurfaceRail: p.rail,
    uiSurfacePopover: p.card,
    uiSurfaceDrawer: p.panel,
    uiSurfaceDrawerHeader: p.panel,
    uiSurfaceDrawerMuted: p.input,
    uiSurfaceInput: p.input,
    uiSurfaceRowHover: alpha(neutral, 0.045),
    uiSurfaceSelected: alpha(p.accent, dark ? 0.09 : 0.08),
    uiBorderDefault: alpha(neutral, dark ? 0.14 : 0.18),
    uiBorderSubtle: alpha(neutral, dark ? 0.08 : 0.09),
    uiBorderStrong: alpha(neutral, dark ? 0.24 : 0.28),
    uiBorderAccent: alpha(p.accent, 0.48),
    uiTextPrimary: p.ink,
    uiTextSecondary: p.secondaryInk,
    uiTextTertiary: p.mutedInk,
    uiTextAccent: p.accent,
    accentSolid: p.action,
    accentOnSolid: p.onAction,
    accentFg: p.accent,
    accentEmphasized: p.actionHover,
    accentFocusRing: p.accent,
    accentMuted: alpha(p.accent, 0.22),
    accentSubtle: alpha(p.accent, 0.1),
    accentChip: alpha(p.accent, dark ? 0.09 : 0.06),
    accentTraceSurface: alpha(p.accent, 0.04),
    accentTraceSurfaceHover: alpha(p.accent, 0.07),
    accentTraceBorder: alpha(p.accent, 0.14),
    accentTraceBorderHover: alpha(p.accent, 0.24),
    accentAnswerHoverBorder: alpha(p.accent, 0.3),
    accentSecondarySubtle: alpha(p.accent, 0.08),
    accentCommandBorder: alpha(p.accent, 0.4),
    accentStart: p.action,
    accentEnd: p.actionHover,
    accentShadow: alpha(p.action, 0.12),
    accentShadowHover: alpha(p.action, 0.18),
    selectionBg: alpha(p.accent, dark ? 0.24 : 0.16),
    scrollbarThumb: alpha(neutral, 0.18),
    scrollbarThumbHover: alpha(neutral, 0.3),
    shadowElevated: dark
      ? '0 2px 8px rgba(0, 0, 0, 0.22)'
      : '0 1px 2px rgba(20, 24, 30, 0.035), 0 4px 12px rgba(20, 24, 30, 0.025)',
    shadowFloating: dark
      ? '0 12px 32px rgba(0, 0, 0, 0.4)'
      : '0 2px 4px rgba(20, 24, 30, 0.04), 0 12px 32px rgba(20, 24, 30, 0.08)',
    shadowSubtle: dark ? '0 1px 3px rgba(0, 0, 0, 0.2)' : '0 1px 2px rgba(20, 24, 30, 0.04)',
    shadowGlow: 'none',
    shadowRing: 'none',
  };
}

export function getGetdesignDeveloperToolThemeCss(
  baseL: DesignThemeCssTokens,
  baseD: DesignThemeCssTokens
) {
  function pair(light: Palette, dark: Palette) {
    return { light: theme(baseL, light, false), dark: theme(baseD, dark, true) };
  }
  return {
    cursor: pair(
      {
        bg: '#ECECE7',
        panel: '#FAFAF7',
        card: '#FFFFFF',
        rail: '#F6F6F2',
        input: '#F0F0EB',
        ink: '#26251E',
        secondaryInk: '#55544C',
        mutedInk: '#6D6B62',
        action: '#D04200',
        actionHover: '#B93800',
        onAction: '#FFFFFF',
        accent: '#B93800',
      },
      {
        bg: '#101110',
        panel: '#1B1C1A',
        card: '#242522',
        rail: '#191A18',
        input: '#1D1D1D',
        ink: '#EFF2F6',
        secondaryInk: '#C0C8D2',
        mutedInk: '#9DA9B8',
        action: '#D04200',
        actionHover: '#B93800',
        onAction: '#FFFFFF',
        accent: '#F09A72',
      }
    ),
    raycast: pair(
      {
        bg: '#EAEDF3',
        panel: '#FCFCFE',
        card: '#FFFFFF',
        rail: '#F6F7FA',
        input: '#EDF1F8',
        ink: '#19243B',
        secondaryInk: '#4E5C75',
        mutedInk: '#5D6982',
        action: '#214CE5',
        actionHover: '#173BBF',
        onAction: '#FFFFFF',
        accent: '#2145CC',
      },
      {
        bg: '#0D1016',
        panel: '#191D25',
        card: '#242934',
        rail: '#171B23',
        input: '#1C1F25',
        ink: '#EFF2F6',
        secondaryInk: '#C0C8D2',
        mutedInk: '#9DA9B8',
        action: '#3458D4',
        actionHover: '#2949B8',
        onAction: '#FFFFFF',
        accent: '#96ADEE',
      }
    ),
    warp: pair(
      {
        bg: '#EAE4D6',
        panel: '#FAF8F2',
        card: '#FFFEF8',
        rail: '#F5F2E9',
        input: '#EEE9DB',
        ink: '#343025',
        secondaryInk: '#605A48',
        mutedInk: '#645B45',
        action: '#795200',
        actionHover: '#604100',
        onAction: '#FFFFFF',
        accent: '#735000',
      },
      {
        bg: '#14120E',
        panel: '#201D17',
        card: '#2B2720',
        rail: '#1D1B15',
        input: '#21201C',
        ink: '#EFF2F6',
        secondaryInk: '#C0C8D2',
        mutedInk: '#9DA9B8',
        action: '#8C671D',
        actionHover: '#765514',
        onAction: '#FFFFFF',
        accent: '#D8B66F',
      }
    ),
    vercel: pair(
      {
        bg: '#E6ECE8',
        panel: '#F9FBFA',
        card: '#FFFFFF',
        rail: '#F3F7F4',
        input: '#EAF2ED',
        ink: '#1D3429',
        secondaryInk: '#486454',
        mutedInk: '#586D60',
        action: '#006747',
        actionHover: '#004E36',
        onAction: '#FFFFFF',
        accent: '#006044',
      },
      {
        bg: '#0E1411',
        panel: '#19221D',
        card: '#242E28',
        rail: '#17201B',
        input: '#1D221F',
        ink: '#EFF2F6',
        secondaryInk: '#C0C8D2',
        mutedInk: '#9DA9B8',
        action: '#256D50',
        actionHover: '#1B573E',
        onAction: '#FFFFFF',
        accent: '#82C7A4',
      }
    ),
    expo: pair(
      {
        bg: '#E5ECEE',
        panel: '#F9FBFC',
        card: '#FFFFFF',
        rail: '#F2F7F8',
        input: '#E7F1F3',
        ink: '#173740',
        secondaryInk: '#46646C',
        mutedInk: '#536C73',
        action: '#00677D',
        actionHover: '#004F63',
        onAction: '#FFFFFF',
        accent: '#006176',
      },
      {
        bg: '#0E1418',
        panel: '#192228',
        card: '#242F36',
        rail: '#172026',
        input: '#1C2227',
        ink: '#EFF2F6',
        secondaryInk: '#C0C8D2',
        mutedInk: '#9DA9B8',
        action: '#166A7B',
        actionHover: '#105565',
        onAction: '#FFFFFF',
        accent: '#83C5D1',
      }
    ),
    lovable: pair(
      {
        bg: '#EFE5E0',
        panel: '#FCF9F7',
        card: '#FFFCFA',
        rail: '#F8F2EE',
        input: '#F3E9E2',
        ink: '#402D27',
        secondaryInk: '#71574D',
        mutedInk: '#71574E',
        action: '#AD3F43',
        actionHover: '#8F2D36',
        onAction: '#FFFFFF',
        accent: '#A0363B',
      },
      {
        bg: '#17110F',
        panel: '#251D19',
        card: '#302620',
        rail: '#221A17',
        input: '#241E1A',
        ink: '#EFF2F6',
        secondaryInk: '#C0C8D2',
        mutedInk: '#9DA9B8',
        action: '#995938',
        actionHover: '#81492E',
        onAction: '#FFFFFF',
        accent: '#DDA989',
      }
    ),
    superhuman: pair(
      {
        bg: '#EAE7EF',
        panel: '#FAF9FC',
        card: '#FFFFFF',
        rail: '#F5F3F8',
        input: '#EFEBF5',
        ink: '#30283F',
        secondaryInk: '#63566F',
        mutedInk: '#665472',
        action: '#7539B5',
        actionHover: '#5F279B',
        onAction: '#FFFFFF',
        accent: '#6D32AA',
      },
      {
        bg: '#121016',
        panel: '#201C27',
        card: '#2C2634',
        rail: '#1D1923',
        input: '#211C27',
        ink: '#EFF2F6',
        secondaryInk: '#C0C8D2',
        mutedInk: '#9DA9B8',
        action: '#655293',
        actionHover: '#534278',
        onAction: '#FFFFFF',
        accent: '#B9A7DA',
      }
    ),
  };
}

/** Functional hues retain their meaning. The preset accent is for action and selection. */
export function withSemanticThemeColors(
  tokens: DesignThemeCssTokens,
  mode: 'light' | 'dark'
): DesignThemeCssTokens {
  const dark = mode === 'dark';
  const hues = dark
    ? {
        blue: '#94B9FF',
        purple: '#C3AAED',
        pink: '#F1A3C5',
        orange: '#F3B278',
        green: '#91D5AB',
        teal: '#82D2C3',
        cyan: '#8ACDDD',
        indigo: '#B0B9F5',
      }
    : {
        blue: '#315DA8',
        purple: '#7952A3',
        pink: '#AD4675',
        orange: '#A85517',
        green: '#267347',
        teal: '#14756A',
        cyan: '#16728A',
        indigo: '#5557A5',
      };
  const success = dark ? '#91D5AB' : '#21633E';
  const warning = dark ? '#F0C782' : '#83540D';
  const danger = dark ? '#F2A3A3' : '#AA3434';
  const info = dark ? '#A4C4F4' : '#315C95';
  const neutral = tokens.uiTextSecondary;
  const gold = dark ? '#E5C582' : '#805B18';
  const silver = dark ? '#C0C8D4' : '#536277';
  const bronze = dark ? '#E4B395' : '#8C5133';
  const result = { ...tokens };
  for (const [name, color] of Object.entries(hues)) {
    const key = `luminous${name[0].toUpperCase()}${name.slice(1)}` as keyof DesignThemeCssTokens;
    result[key] = color;
    result[`${key}Glow` as keyof DesignThemeCssTokens] = alpha(color, 0.2);
  }
  for (const [name, color] of Object.entries({
    Success: success,
    Warning: warning,
    Danger: danger,
    Info: info,
    Neutral: neutral,
  })) {
    result[`status${name}Fg` as keyof DesignThemeCssTokens] = color;
    result[`status${name}Bg` as keyof DesignThemeCssTokens] = alpha(color, dark ? 0.1 : 0.065);
    result[`status${name}Border` as keyof DesignThemeCssTokens] = alpha(color, dark ? 0.24 : 0.2);
  }
  for (const [name, color] of Object.entries({ Gold: gold, Silver: silver, Bronze: bronze })) {
    result[`rank${name}Fg` as keyof DesignThemeCssTokens] = color;
    result[`rank${name}Bg` as keyof DesignThemeCssTokens] = alpha(color, 0.08);
    result[`rank${name}Border` as keyof DesignThemeCssTokens] = alpha(color, 0.22);
  }
  const series = [
    tokens.accentFg,
    hues.blue,
    hues.teal,
    hues.purple,
    hues.orange,
    hues.pink,
    hues.green,
    hues.indigo,
    hues.cyan,
    dark ? '#CCCD89' : '#70712F',
    dark ? '#CFA999' : '#895B48',
    dark ? '#9EB2C2' : '#536C80',
    dark ? '#E1A1AB' : '#A54659',
    dark ? '#AFCA98' : '#587D38',
    dark ? '#C6AEC9' : '#816187',
  ];
  series.forEach((color, index) => {
    result[`chartSeries${index + 1}` as keyof DesignThemeCssTokens] = color;
  });
  return {
    ...result,
    uiSurfaceWarning: result.statusWarningBg,
    dataVisibility: tokens.accentFg,
    dataRank1: gold,
    dataRank3: bronze,
    dataMissed: danger,
    dataPositive: success,
    dataNegative: danger,
    dataNeutral: neutral,
    dataOrange: hues.orange,
    dataPurple: hues.purple,
    dataCyan: hues.cyan,
    dataPink: hues.pink,
    dataTooltipBg: tokens.uiSurfacePopover,
    dataGrid: tokens.uiBorderSubtle,
    dataAxis: tokens.uiTextTertiary,
    dataCursor: tokens.uiSurfaceRowHover,
  };
}
